import React, { useMemo, useState } from "react";
import { eachDayOfInterval, formatISO, parseISO } from "date-fns";
import { CalendarHeatmap } from "@design-system/CalendarHeatmap/CalendarHeatmap";
import { CalendarHeatmapDatum } from "@design-system/CalendarHeatmap/CalendarHeatmap.lib";
import { PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DataDisplayPlayground.module.scss";

type RangePreset = "month" | "quarter" | "year";

const rangePresetOptions = [
    "month",
    "quarter",
    "year",
] satisfies readonly RangePreset[];

const RANGE_BOUNDS: Record<
    RangePreset,
    { startDate: string; endDate: string }
> = {
    month: { startDate: "2026-03-01", endDate: "2026-03-31" },
    quarter: { startDate: "2026-01-01", endDate: "2026-03-31" },
    year: { startDate: "2026-01-01", endDate: "2026-12-31" },
};

function buildDemoData(
    startDate: string,
    endDate: string,
): CalendarHeatmapDatum[] {
    const values = [0, 0, 1, 2, 0, 4, 1, 0, 8, 3];
    return eachDayOfInterval({
        start: parseISO(startDate),
        end: parseISO(endDate),
    }).flatMap((day, index) => {
        const value = values[index % values.length] ?? 0;
        if (value <= 0) {
            return [];
        }
        return [
            {
                date: formatISO(day, { representation: "date" }),
                value,
            },
        ];
    });
}

export const CalendarHeatmapPlayground = () => {
    const [rangePreset, setRangePreset] = useState<RangePreset>("year");
    const range = RANGE_BOUNDS[rangePreset];
    const data = useMemo(
        () => buildDemoData(range.startDate, range.endDate),
        [range.endDate, range.startDate],
    );

    return (
        <SandboxLayout
            controls={
                <section>
                    <h2>Range</h2>
                    <PlaygroundSelect<RangePreset>
                        options={rangePresetOptions}
                        value={rangePreset}
                        onChange={setRangePreset}
                    />
                </section>
            }
        >
            <ShowcasePreview
                summary={`${data.length} active days`}
                output={JSON.stringify(
                    {
                        props: {
                            startDate: range.startDate,
                            endDate: range.endDate,
                            dataCount: data.length,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.heatmapSurface}>
                    <CalendarHeatmap
                        data={data}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        ariaLabel="Calendar heatmap playground"
                    />
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
